import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Logo, TikTokIcon } from './bits';

export default function Footer() {
  const { t } = useLanguage();

  const navLinks = [
    { to: '/', label: t.nav.home, id: 'home' },
    { to: '/services', label: t.nav.services, id: 'services' },
    { to: '/about', label: t.nav.about, id: 'about' },
    { to: '/seasonal', label: t.nav.seasonal, id: 'seasonal' },
    { to: '/contact', label: t.nav.contact, id: 'contact' },
  ];

  const socials = [
    { name: 'Instagram', icon: Instagram, id: 'instagram', href: 'https://www.instagram.com/entretien.deluxe' },
    { name: 'TikTok', icon: TikTokIcon, id: 'tiktok', href: 'https://www.tiktok.com/@deluxe.entretien' },
  ];

  return (
    <footer className="bg-ink text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 sm:px-12 lg:px-20 lg:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo footer />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/60" data-testid="footer-tagline">
              {t.footer.tagline}
            </p>
            <div className="mt-8 flex items-center gap-4">
              {socials.map(({ name, icon: Icon, id, href }) =>
                href ? (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={name}
                    data-testid={`social-${id}`}
                    className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/60 transition-colors duration-300 hover:border-[#C6A15B] hover:text-[#C6A15B]"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ) : (
                  <span
                    key={id}
                    title={`${name} — ${t.footer.socialSoon}`}
                    aria-label={`${name} (${t.footer.socialSoon})`}
                    data-testid={`social-${id}`}
                    className="flex h-10 w-10 cursor-not-allowed items-center justify-center border border-white/15 text-white/40"
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                )
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C6A15B]">
              {t.footer.navigate}
            </h3>
            <ul className="mt-5 space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <Link
                    to={l.to}
                    data-testid={`footer-nav-${l.id}`}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C6A15B]">
              {t.footer.services}
            </h3>
            <ul className="mt-5 space-y-3">
              {t.services.items.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/services#${s.id}`}
                    data-testid={`footer-service-${s.id}`}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {s.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C6A15B]">
              {t.footer.contact}
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-white/60">
              <li data-testid="footer-phone">
                <a
                  href={`tel:+1${t.contactPage.phoneEn.replace(/[^0-9]/g, '')}`}
                  className="block transition-colors hover:text-white"
                >
                  EN — {t.contactPage.phoneEn}
                </a>
                <a
                  href={`tel:+1${t.contactPage.phoneFr.replace(/[^0-9]/g, '')}`}
                  className="mt-1 block transition-colors hover:text-white"
                >
                  FR — {t.contactPage.phoneFr}
                </a>
              </li>
              <li data-testid="footer-email">
                <a
                  href={`mailto:${t.contactPage.emailValue}`}
                  className="break-all transition-colors hover:text-white"
                >
                  {t.contactPage.emailValue}
                </a>
              </li>
              <li data-testid="footer-area">{t.footer.location}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-white/40" data-testid="footer-copyright">{t.footer.rights}</p>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <Link to="/privacy" data-testid="footer-privacy" className="transition-colors hover:text-white">
              {t.footer.privacy}
            </Link>
            <Link to="/terms" data-testid="footer-terms" className="transition-colors hover:text-white">
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
